/**
 * Goal 2 report-pilot: finish_report as one dialog-owned operation.
 * Steps advance only from confirmed API results — never from can_be_completed / timers.
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  classifyHttpSuccess,
  canConfirmStepFromEvidence,
  mapServerStepStatus,
  aggregateOperationOutcome,
  outcomeAfterRefreshFailure,
} from "../../../../operationFeedback";

export const FINISH_REPORT_OPERATION_KEY = "finish_report";

/** Readiness (preflight) — not execution progress. */
export function evaluateFinishReadiness(report) {
  const ops = Array.isArray(report?.operations) ? report.operations : [];
  const finishOps = ops.filter((o) => o?.name === "finish_report");
  if (finishOps.length === 0) {
    return { ready: true, blockers: [], checked: false };
  }
  const blockers = finishOps.filter((o) => o.can_be_completed === false);
  return {
    ready: blockers.length === 0,
    blockers,
    checked: true,
  };
}

export function isFinishReportErrorPayload(payload) {
  if (!payload) return true;
  if (payload.isAxiosError) return true;
  if (payload.response?.status && payload.response.status >= 400) return true;
  if (hasMeaningfulErrors(payload.errors)) return true;
  if (payload.error === true) return true;
  // Empty errors: [] / {} are not failures (classifyHttpSuccess / SPEC §7).
  if (payload.errors != null) {
    const classified = classifyHttpSuccess(
      payload.status || payload.httpStatus || 200,
      payload
    );
    if (!classified.ok) return true;
  }
  return false;
}

export function isFinishReportSuccessPayload(payload) {
  if (isFinishReportErrorPayload(payload)) return false;

  const feedback = extractOperationFeedback(payload);
  if (feedback?.outcome === OUTCOME.APPLIED || feedback?.outcome === "applied") {
    return true;
  }
  if (feedback?.outcome === OUTCOME.PARTIAL || feedback?.outcome === "partial") {
    return true;
  }
  if (
    Array.isArray(feedback?.steps) &&
    feedback.steps.some((s) => {
      const status = mapServerStepStatus(s);
      return (
        status === STEP_STATUS.COMPLETED ||
        status === STEP_STATUS.ALREADY_APPLIED
      );
    })
  ) {
    return true;
  }

  const data = payload?.data || payload;
  if (data?.finished === true || data?.finished === "true") return true;
  const messages = payload?.messages;
  if (
    Array.isArray(messages) &&
    messages.some((m) => /report was finished|already finished|allerede ferdig/i.test(String(m)))
  ) {
    return true;
  }
  // Successful PUT with report id and no errors (legacy happy path)
  if (data?.id && !hasMeaningfulErrors(payload?.errors) && payload?.messages) {
    return messages.some((m) => /finished|ferdig/i.test(String(m)));
  }
  return Boolean(data?.id && data?.finished);
}

export function extractOperationFeedback(payload) {
  return (
    payload?.operation_feedback ||
    payload?.operation ||
    payload?.data?.operation_feedback ||
    payload?.data?.operation ||
    null
  );
}

/**
 * Map server step keys onto the client step list (save_finish → save).
 * @param {string} key
 */
export function normalizeFinishStepKey(key) {
  if (key === "save_finish") return "save";
  return key;
}

function defaultExecutionSteps(t) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.finish_report.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "save",
      label: t("operation_feedback.finish_report.step.save"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "finish_checklist",
      label: t("operation_feedback.finish_report.step.finish_checklist"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "update_agreement",
      label: t("operation_feedback.finish_report.step.update_agreement"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "refresh",
      label: t("operation_feedback.finish_report.step.refresh"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

function applyConfirmedServerSteps(coordinator, clientOperationId, serverOp, revStart) {
  let rev = revStart;
  if (!serverOp || !Array.isArray(serverOp.steps)) {
    return { rev, applied: false };
  }

  const mapped = [];
  serverOp.steps.forEach((raw) => {
    if (!raw?.key) return;
    const status = mapServerStepStatus(raw);
    if (
      !canConfirmStepFromEvidence({
        status,
        fromServerResult: true,
      })
    ) {
      return;
    }
    // Local revisions must stay monotonic — server step revision alone can be
    // lower than a prior RUNNING update and would otherwise be ignored.
    const nextRev = Math.max(
      rev,
      raw.revision != null ? Number(raw.revision) : 0
    );
    mapped.push({
      key: normalizeFinishStepKey(raw.key),
      label: raw.label || raw.description,
      status,
      detail: raw.detail,
      result_ref: raw.result_ref || raw.resultRef,
      revision: nextRev,
    });
    rev = nextRev + 1;
  });

  if (mapped.length === 0) {
    return { rev, applied: false };
  }

  const payloadRev = Math.max(
    rev,
    serverOp.revision != null ? Number(serverOp.revision) : 0
  );
  coordinator.applyServerStatus(clientOperationId, {
    revision: payloadRev,
    serverOperationId: serverOp.id || serverOp.server_operation_id,
    steps: mapped,
  });
  return { rev: payloadRev + 1, applied: true, steps: mapped };
}

/**
 * @param {object} args
 * @param {Function} args.dispatch
 * @param {Function} args.updateReportAsync - RTK thunk
 * @param {object} args.values - form values (id, finished, finished_date)
 * @param {object} [args.report]
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onRefresh] - optional list/detail refresh after save
 * @param {Function} args.t - i18n t()
 * @returns {Promise<{ clientOperationId: string, ok: boolean, payload?: unknown, error?: object }>}
 */
export async function runFinishReportOperation({
  dispatch,
  updateReportAsync,
  values,
  report,
  companyId,
  onRefresh,
  t,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = values?.id || report?.id;
  const title = t("operation_feedback.finish_report.title", {
    number: report?.report_number || report?.displayTitle || resourceId || "",
  });

  const op = coordinator.start({
    operationKey: FINISH_REPORT_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const readiness = evaluateFinishReadiness(report);
  // Readiness must never be treated as confirmed execution success.
  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional: gate confirms invariants reject readiness-as-complete */
  }

  if (readiness.checked && !readiness.ready) {
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail: t("operation_feedback.finish_report.preflight_blocked"),
      revision: rev++,
    });
    coordinator.updateStep(id, "save", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "finish_checklist", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.updateStep(id, "update_agreement", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: t("operation_feedback.finish_report.preflight_blocked"),
      outcome: OUTCOME.NOT_APPLIED,
      links: resourceId
        ? [
            {
              href: `/dashboard/service/reports/${resourceId}`,
              label: t("operation_feedback.finish_report.open_report"),
            },
          ]
        : [],
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: readiness.checked ? STEP_STATUS.COMPLETED : STEP_STATUS.NOT_RELEVANT,
    detail: readiness.checked
      ? t("operation_feedback.finish_report.preflight_ok")
      : t("operation_feedback.finish_report.preflight_skipped"),
    revision: rev++,
  });

  coordinator.updateStep(id, "save", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let action;
  try {
    action = await dispatch(updateReportAsync(values));
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: FINISH_REPORT_OPERATION_KEY,
    });
    coordinator.updateStep(id, "save", {
      status: normalized.outcome === OUTCOME.UNKNOWN ? STEP_STATUS.UNCONFIRMED : STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "finish_checklist", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "update_agreement", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: normalized.outcome,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = action?.payload;
  const rejected = action?.meta?.requestStatus === "rejected";

  if (
    rejected ||
    isFinishReportErrorPayload(payload) ||
    !isFinishReportSuccessPayload(payload)
  ) {
    const normalized = normalizeError(
      rejected
        ? payload ?? action?.error
        : payload?.response
          ? payload
          : { response: { status: 422, data: payload } },
      { operationKey: FINISH_REPORT_OPERATION_KEY }
    );
    const feedback = extractOperationFeedback(
      payload?.response?.data || payload
    );
    if (feedback?.steps) {
      const applied = applyConfirmedServerSteps(
        coordinator,
        id,
        feedback,
        rev
      );
      rev = applied.rev;
    } else {
      coordinator.updateStep(id, "save", {
        status: normalized.outcome === OUTCOME.UNKNOWN ? STEP_STATUS.UNCONFIRMED : STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
    }
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: normalized.outcome,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  // Prefer server-confirmed operation_feedback steps when present.
  const serverOp = extractOperationFeedback(payload);
  const applied = applyConfirmedServerSteps(coordinator, id, serverOp, rev);
  rev = applied.rev;

  if (!applied.applied) {
    if (
      canConfirmStepFromEvidence({
        status: STEP_STATUS.COMPLETED,
        fromConfirmedApiResult: true,
      })
    ) {
      coordinator.updateStep(id, "save", {
        status: STEP_STATUS.COMPLETED,
        detail: t("operation_feedback.finish_report.save_ok"),
        resultRef: { id: (payload?.data || payload)?.id },
        revision: rev++,
      });
    }
    // No server checklist/agreement detail — mark not_relevant (not failure).
    coordinator.updateStep(id, "finish_checklist", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.updateStep(id, "update_agreement", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
  }

  const current = coordinator.getOperation(id);
  const saveStep = current?.steps?.find((s) => s.key === "save");
  const saveStatus = saveStep?.status || STEP_STATUS.COMPLETED;

  const baseOutcome =
    serverOp?.outcome ||
    aggregateOperationOutcome(
      (current?.steps || []).filter(
        (s) => s.key !== "preflight" && s.key !== "refresh"
      )
    ).outcome;

  const reportLinks = resourceId
    ? [
        {
          href: `/dashboard/service/reports/${resourceId}`,
          label: t("operation_feedback.finish_report.open_report"),
        },
      ]
    : [];

  // Optional UI refresh — failure must not rewrite a confirmed save as failure.
  if (typeof onRefresh === "function") {
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await Promise.resolve(onRefresh(payload?.data || payload));
      coordinator.updateStep(id, "refresh", {
        status: STEP_STATUS.COMPLETED,
        revision: rev++,
      });
      const afterOk = outcomeAfterRefreshFailure(saveStatus, false);
      coordinator.complete(id, {
        summary: t("operation_feedback.finish_report.success"),
        outcome: baseOutcome || afterOk.outcome || OUTCOME.APPLIED,
        links: reportLinks,
      });
      return { clientOperationId: id, ok: true, payload };
    } catch (refreshErr) {
      const normalized = normalizeError(refreshErr, {
        operationKey: FINISH_REPORT_OPERATION_KEY,
      });
      const afterFail = outcomeAfterRefreshFailure(saveStatus, true);
      coordinator.updateStep(id, "refresh", {
        status: afterFail.refreshStatus || STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
      // Keep confirmed save status intact.
      if (
        saveStep &&
        afterFail.saveStatus &&
        afterFail.saveStatus !== saveStep.status
      ) {
        /* never downgrade — outcomeAfterRefreshFailure preserves success */
      }
      coordinator.complete(id, {
        summary: t(
          afterFail.summaryKey || "operation_feedback.saved_list_refresh_failed"
        ),
        outcome: afterFail.outcome || OUTCOME.PARTIAL,
        links: reportLinks,
      });
      return { clientOperationId: id, ok: true, payload, refreshFailed: true };
    }
  }

  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_RELEVANT,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary: t("operation_feedback.finish_report.success"),
    outcome: baseOutcome || OUTCOME.APPLIED,
    links: reportLinks,
  });
  return { clientOperationId: id, ok: true, payload };
}
